import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ScoreBadge, SourceTag, formatDate, formatGBP, daysUntil } from "../lib/format";
import { ArrowLeft, Bookmark, BookmarkCheck, ExternalLink, AlertTriangle } from "lucide-react";

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracked, setTracked] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get(`/notices/${id}`).then((r) => r.data),
      api.get("/tracked").then((r) => r.data).catch(() => []),
    ]).then(([n, tlist]) => {
      if (cancel) return;
      setNotice(n);
      const t = tlist.find((t) => t.notice.id === id);
      setTracked(t || null);
      setLoading(false);
    }).catch((e) => {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    });
    return () => { cancel = true; };
  }, [id]);

  const track = async () => {
    setBusy(true);
    try {
      await api.post("/tracked", { notice_id: id });
      const tlist = (await api.get("/tracked")).data;
      const t = tlist.find((t) => t.notice.id === id);
      setTracked(t || null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const untrack = async () => {
    setBusy(true);
    try {
      await api.delete(`/tracked/${id}`);
      setTracked(null);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div><span className="spinner" /></div>;
  if (!notice) return <div className="empty-state"><h3>Notice not found</h3><p>{error || "The notice may have been removed."}</p></div>;

  return (
    <div data-testid="opportunity-detail-page">
      <button onClick={() => navigate(-1)} className="btn-ghost mb-4" data-testid="back-button"><ArrowLeft size={14} /> Back</button>

      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <SourceTag source={notice.source} url={notice.source_url} />
            {notice.published_at && (
              <span className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
                Published {formatDate(notice.published_at)}
              </span>
            )}
            {notice.data_quality_warnings?.length > 0 && (
              <span className="status-pill" title={notice.data_quality_warnings.join("; ")} data-testid="data-quality-warnings">
                <AlertTriangle size={11} /> {notice.data_quality_warnings.length} data note(s)
              </span>
            )}
          </div>
          <h1 className="text-[26px] leading-tight font-semibold tracking-tight" data-testid="notice-title">{notice.title}</h1>
          <div className="mt-2 text-[13px]" style={{ color: "var(--parapet-text-secondary)" }}>
            {notice.buyer_name || "Buyer not published"}
            {notice.stage && <> · {notice.stage}</>}
          </div>
        </div>

        <div className="p-card" style={{ minWidth: 280 }} data-testid="fit-score-card">
          <div className="flex items-center justify-between mb-2">
            <span className="label-meta">Fit score</span>
            <ScoreBadge score={notice.fit?.score} band={notice.fit?.band} insufficient={notice.fit?.insufficient_data} />
          </div>
          <div className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
            {notice.fit?.insufficient_data
              ? "Some dimensions lack source data — normalised on what's available."
              : "Computed from your company profile and this notice."}
          </div>
          <div className="mt-3 space-y-2">
            {(notice.fit?.dimensions || []).map((d) => (
              <div key={d.name} className="text-[12px]" data-testid={`fit-dim-${d.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{d.name}</span>
                  <span className="value-mono">
                    {d.data_available
                      ? `${Math.round(d.raw_score * 100)}/100 · ${(d.weight * 100).toFixed(0)}%`
                      : "Insufficient data"}
                  </span>
                </div>
                <div style={{ color: "var(--parapet-text-secondary)", marginTop: 2 }}>{d.rationale}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {tracked ? (
          <button className="btn-secondary" onClick={untrack} disabled={busy} data-testid="untrack-button">
            <BookmarkCheck size={14} className="inline mr-1" /> Tracking — click to remove
          </button>
        ) : (
          <button className="btn-primary" onClick={track} disabled={busy} data-testid="track-button">
            <Bookmark size={14} className="inline mr-1" /> Track this opportunity
          </button>
        )}
        {notice.source_url && (
          <a href={notice.source_url} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="open-source-button">
            <ExternalLink size={14} className="inline mr-1" /> Open source notice
          </a>
        )}
      </div>

      {error && <div className="text-[12px] p-2 border mb-4" style={{ borderColor: "#111" }}>{error}</div>}

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-4">
          <div className="p-card">
            <h2 className="mb-3">Description</h2>
            {notice.description ? (
              <p className="text-[14px] whitespace-pre-wrap leading-relaxed">{notice.description}</p>
            ) : (
              <p className="text-[13px]" style={{ color: "var(--parapet-text-secondary)" }}>Not published in source.</p>
            )}
          </div>

          <div className="p-card" data-testid="checklist-card">
            <ChecklistView noticeId={id} tracked={tracked} />
          </div>

          {(notice.awards?.length || 0) > 0 && (
            <div className="p-card">
              <h2 className="mb-3">Awards & suppliers</h2>
              <table className="p-table">
                <thead>
                  <tr><th>Supplier</th><th>Value</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {notice.awards.flatMap((a) => (a.suppliers || []).map((s) => ({ ...s, ...a }))).map((s) => (
                    <tr key={`${s.name || "unknown"}|${s.date || ""}|${s.value_amount || ""}`}>
                      <td className="font-medium">{s.name || "—"}</td>
                      <td className="value-mono">{s.value_amount != null ? formatGBP(s.value_amount, s.value_currency) : "—"}</td>
                      <td className="text-[12px]">{s.date ? formatDate(s.date) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="p-card">
            <h2 className="mb-3">Key facts</h2>
            <KeyValue label="Source ref" value={<span className="value-mono">{notice.source_ref}</span>} />
            <KeyValue label="Buyer" value={notice.buyer_name || "Not published"} />
            <KeyValue label="Value" value={notice.value_amount != null ? formatGBP(notice.value_amount, notice.value_currency) : "Not published"} />
            <KeyValue label="Stage" value={notice.stage || "—"} />
            <KeyValue label="Procurement method" value={notice.procurement_method || "Not published"} />
            <KeyValue label="Deadline" value={notice.deadline ? `${formatDate(notice.deadline)} (${daysUntil(notice.deadline)}d)` : "Not published"} />
          </div>

          {(notice.cpv_codes || []).length > 0 && (
            <div className="p-card">
              <h2 className="mb-2">CPV codes</h2>
              <div className="flex flex-wrap gap-1">
                {notice.cpv_codes.map((c) => (
                  <span key={c} className="value-mono text-[12px] px-2 py-1" style={{ background: "var(--parapet-surface)", borderRadius: 4 }}>{c}</span>
                ))}
              </div>
            </div>
          )}

          {notice.data_quality_warnings?.length > 0 && (
            <div className="p-card">
              <h2 className="mb-2">Data quality</h2>
              {notice.data_quality_warnings.map((w) => (
                <div key={w} className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>· {w}</div>
              ))}
            </div>
          )}

          {(notice.versions || []).length > 0 && (
            <div className="p-card">
              <h2 className="mb-2">Change history</h2>
              {notice.versions.map((v) => (
                <div key={v.captured_at} className="text-[12px] py-1" style={{ color: "var(--parapet-text-secondary)" }}>
                  · Updated {formatDate(v.captured_at)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KeyValue({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b last:border-0" style={{ borderColor: "var(--parapet-grid)" }}>
      <span className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>{label}</span>
      <span className="text-[13px] text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function ChecklistView({ noticeId, tracked }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Always fetch the live notice-level checklist (no track required).
  // If the user has tracked the notice, we still display the live version —
  // their per-item toggled state will be reflected only on tracked rows.
  useEffect(() => {
    setLoading(true);
    api
      .get(`/notices/${noticeId}/checklist`)
      .then((r) => {
        // Hide items the engine has already determined don't apply to this
        // notice — they add noise and aren't actionable for the user.
        const visible = (r.data?.items || []).filter((it) => it.status !== "not_applicable");
        setItems(visible);
        setSummary(r.data?.summary || null);
      })
      .catch(() => { setItems([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [noticeId]);

  const toggle = async (it) => {
    if (!tracked) return; // toggling only persists when tracked
    const next = it.status === "done" ? "open" : "done";
    try {
      await api.put(`/tracked/${noticeId}/checklist/${it.id}`, { status: next });
      setItems(items.map((x) => x.id === it.id ? { ...x, status: next } : x));
    } catch (_) { /* surface inline if needed */ }
  };

  const groups = {};
  items.forEach((i) => { (groups[i.category] = groups[i.category] || []).push(i); });

  return (
    <div data-testid="checklist-view">
      <div className="flex items-baseline justify-between mb-2">
        <h2>Bid-readiness checklist</h2>
        {summary && (
          <div className="flex gap-2 text-[11px]" style={{ color: "var(--parapet-text-secondary)" }}>
            <span><b style={{ color: "#7a0606" }}>{summary.blocked}</b> blocked</span>
            <span>·</span>
            <span><b style={{ color: "#0a6b2a" }}>{summary.done}</b> done</span>
            <span>·</span>
            <span><b>{summary.mandatory}</b> mandatory</span>
          </div>
        )}
      </div>
      <p className="text-[12px] mb-4" style={{ color: "var(--parapet-text-secondary)" }}>
        Auto-generated from the notice text and your profile. {tracked
          ? "Tick items as you complete them — saved to this tracked tender."
          : "Track this tender to save tick states across sessions."}
      </p>

      {loading ? (
        <div className="py-6 text-center"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
          No requirements detected in the notice text.
        </div>
      ) : Object.entries(groups).map(([cat, list]) => (
        <div key={cat} className="mb-4">
          <div className="label-meta mb-2">{cat.replace(/_/g, " ")}</div>
          {list.map((it) => (
            <ChecklistRow key={it.id} it={it} canToggle={!!tracked} onToggle={toggle} />
          ))}
        </div>
      ))}
    </div>
  );
}

const STATUS_STYLE = {
  blocked:       { bg: "#FDECEC", fg: "#7a0606", label: "Gap" },
  open:          { bg: "#FFF6E5", fg: "#7a4a02", label: "Open" },
  done:          { bg: "#E7F5EB", fg: "#0a6b2a", label: "Done" },
  not_applicable:{ bg: "#F4F4F4", fg: "#666",    label: "N/A" },
};
const CONFIDENCE_STYLE = {
  mandatory: { bg: "#111",    fg: "#fff", label: "Mandatory" },
  desirable: { bg: "#E8E1FF", fg: "#3B0E91", label: "Desirable" },
  mentioned: { bg: "#F4F4F4", fg: "#666", label: "Mentioned" },
};

function StatusPill({ kind, type = "status" }) {
  const s = (type === "status" ? STATUS_STYLE[kind] : CONFIDENCE_STYLE[kind]);
  if (!s) return null;
  return (
    <span
      className="value-mono"
      style={{
        background: s.bg, color: s.fg,
        padding: "2px 7px", borderRadius: 3,
        fontSize: 10, letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
    >
      {s.label}
    </span>
  );
}

function ChecklistRow({ it, canToggle, onToggle }) {
  const disabled = !canToggle || it.status === "not_applicable";
  return (
    <div
      className="flex items-start gap-2 py-2 border-b last:border-0"
      style={{ borderColor: "var(--parapet-grid)" }}
    >
      <input
        type="checkbox"
        checked={it.status === "done"}
        onChange={() => onToggle(it)}
        disabled={disabled}
        style={{ marginTop: 5, accentColor: "#111", cursor: disabled ? "not-allowed" : "pointer" }}
        data-testid={`checklist-item-${it.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[13px] font-medium"
            style={{
              textDecoration: it.status === "done" ? "line-through" : "none",
              color: it.status === "not_applicable" ? "var(--parapet-text-secondary)" : "var(--parapet-text)",
            }}
          >
            {it.label}
          </span>
          <StatusPill kind={it.status} type="status" />
          {it.confidence && it.confidence !== "mentioned" && (
            <StatusPill kind={it.confidence} type="confidence" />
          )}
        </div>
        {it.detail && (
          <div className="text-[12px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
            {it.detail}
          </div>
        )}
      </div>
    </div>
  );
}
